import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api } from "../lib/api";
import { useSession } from "../lib/useSession";

interface Section {
  id: string;
  section: string;
  title: string;
  body: string;
}
interface DictRow {
  tableName: string;
  columnName: string;
  dataType: string;
  description: string;
  isPii: boolean;
}

/**
 * Developer Manual (stack §14d) — the data model as queryable ground truth.
 * Staff and above; the API enforces this independently of the UI.
 */
export default function DeveloperManualViewer() {
  const { t } = useTranslation();
  const { user } = useSession();

  const sections = useQuery({
    queryKey: ["developer-manual"],
    queryFn: () => api<Section[]>("/api/v1/developer-manual"),
    enabled: Boolean(user),
  });

  const dictionary = useQuery({
    queryKey: ["data-dictionary"],
    queryFn: () => api<DictRow[]>("/api/v1/data-dictionary"),
    enabled: Boolean(user),
  });

  const byTable = new Map<string, DictRow[]>();
  for (const row of dictionary.data ?? []) {
    const list = byTable.get(row.tableName) ?? [];
    list.push(row);
    byTable.set(row.tableName, list);
  }

  if (!user) return <p className="text-muted-foreground">{t("auth:signInPrompt")}</p>;
  if (sections.isError) return <p className="text-destructive">{t("common:staffOnly")}</p>;

  return (
    <>
      <h1>{t("navigation:developerManual")}</h1>

      {(sections.data ?? []).map((s) => (
        <section key={s.id} className="mt-6">
          <h2 className="text-2xl">{s.title}</h2>
          <div className="mt-2 whitespace-pre-wrap leading-relaxed text-muted-foreground">
            {s.body}
          </div>
        </section>
      ))}

      <h2 className="mt-10">{t("common:dataDictionary")}</h2>
      {[...byTable.entries()].map(([table, rows]) => (
        <section key={table} className="mt-6">
          <h3 className="font-mono text-xl font-semibold">{table}</h3>
          <Card className="mt-2">
            <Table className="min-w-[36rem]">
              <TableHeader>
                <TableRow>
                  <TableHead>{t("common:column")}</TableHead>
                  <TableHead>{t("common:type")}</TableHead>
                  <TableHead>{t("common:description")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.columnName}>
                    <TableCell className="font-mono">{r.columnName}</TableCell>
                    <TableCell className="font-mono text-muted-foreground">{r.dataType}</TableCell>
                    <TableCell>
                      {r.description}
                      {r.isPii && (
                        <Badge variant="warning" className="ml-2">
                          PII
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </section>
      ))}
    </>
  );
}
